/**
 * §L1/§L2 · Opciones de MODALIDAD de una cohorte, filtradas por la vertical
 * presencial.
 *
 * Fuente ÚNICA de las cuatro modalidades y de cuáles se ofrecen: los selectores
 * (crear cohorte, editar cohorte) las consumen de aquí en vez de repetir la
 * lista de `<option>`. Con `academy.modality.in_person` apagada, «presencial» e
 * «híbrida» no se ofrecen — pero una cohorte que YA las tenga sigue mostrando
 * su valor (dataPolicy `preserved`: no reescribimos historia).
 */
import type { Accessor } from "solid-js";
import { createMemo } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  DEFAULT_COHORT_MODALITY,
  MODALITY_IN_PERSON_KEY,
  modalityNeedsInPerson,
} from "@space/shared";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";

export type Modality = "in_person" | "online_live" | "hybrid" | "self_paced";

export interface ModalityOption {
  value: Modality;
  label: MessageDescriptor;
}

/** Las cuatro modalidades del dominio, en orden de presentación online-first. */
const ALL_MODALITIES: readonly ModalityOption[] = [
  { value: "online_live", label: msg`Online live` },
  { value: "self_paced", label: msg`Self-paced` },
  { value: "in_person", label: msg`In person` },
  { value: "hybrid", label: msg`Hybrid` },
];

/** Normaliza un valor crudo de `<select>` a una modalidad válida. */
export function asModality(v: string): Modality {
  return v === "in_person" || v === "hybrid" || v === "self_paced" || v === "online_live"
    ? v
    : DEFAULT_COHORT_MODALITY;
}

/**
 * Opciones ofrecibles en `orgId` (por defecto la org activa). `current` es el
 * valor que ya tiene la entidad: si es presencial/híbrida se conserva en la
 * lista aunque la vertical esté apagada, para que editar OTRO campo de esa
 * cohorte no la reescriba silenciosamente a online.
 */
export function useModalityOptions(args: {
  orgId?: Accessor<string | undefined>;
  current?: Accessor<string | null | undefined>;
}): Accessor<ModalityOption[]> {
  const orgId = () => args.orgId?.();
  const current = () => args.current?.();
  const inPerson = useFeatureEnabled(MODALITY_IN_PERSON_KEY, { orgId });
  const options = createMemo(() => {
    if (inPerson().enabled) {
      return [...ALL_MODALITIES];
    }
    const kept = current();
    return ALL_MODALITIES.filter((o) => !modalityNeedsInPerson(o.value) || o.value === kept);
  });
  return options;
}
